import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Paper,
  Avatar,
  Stack,
  InputAdornment,
  FormHelperText,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  LocalOffer as LocalOfferIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
  CardGiftcard as CardGiftcardIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon,
  TextFields as TextFieldsIcon,
  CheckBox as CheckBoxIcon,
  RadioButtonChecked as RadioIcon,
  ArrowDropDown as SelectIcon,
  Remove as RemoveIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { benefitsService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface FormField {
  id: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'select' | 'radio' | 'checkbox' | 'textarea';
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
}

interface Benefit {
  id: string;
  name: string;
  description: string;
  // Company Information
  companyName?: string;
  companyLogo?: string;
  companyWebsite?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyAddress?: string;

  // Partner Credentials
  partnerLoginEmail?: string;
  partnerLoginPassword?: string;

  // Benefit Details
  benefitType?: string; // 'discount' | 'free_service' | 'voucher' | 'membership' | 'consultation'
  benefitValue?: string; // "Free consultation worth R500" or "20% discount"
  redemptionDetails?: string; // Detailed explanation of what user gets
  howToRedeem?: string; // Step-by-step redemption process

  // Terms & Conditions
  termsAndConditions?: string;
  validFrom?: string;
  validTo?: string;
  usageLimit?: number;
  maxRedemptionsPerUser?: number;

  // Visual & Display
  featuredImage?: string;
  highlights?: string[]; // Bullet points for quick view

  // Redemption Form
  redemptionForm?: FormField[];

  // Status & Tracking
  isActive?: boolean;
  totalRedemptions?: number;

  // Visibility Control
  visible_to_plans?: string[]; // Array of plan names: ['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE']

  // Metadata (for backwards compatibility)
  partner?: string;
  discount?: string;
  points?: number;
  value?: number;
  metadata?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`benefit-tabpanel-${index}`}
      aria-labelledby={`benefit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const BenefitsEnhanced: React.FC = () => {
  const { user } = useAuth();
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState<Benefit | null>(null);
  const [benefitToDelete, setBenefitToDelete] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  const [formData, setFormData] = useState<Partial<Benefit>>({
    name: '',
    description: '',
    companyName: '',
    companyWebsite: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    companyLogo: '',
    partnerLoginEmail: '',
    partnerLoginPassword: '',
    benefitType: 'discount',
    benefitValue: '',
    redemptionDetails: '',
    howToRedeem: '',
    termsAndConditions: '',
    validFrom: new Date().toISOString().split('T')[0],
    validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    maxRedemptionsPerUser: 1,
    highlights: [''],
    redemptionForm: [],
    isActive: true,
    visible_to_plans: ['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE'] // Default: visible to all plans
  });

  const [formFieldDialog, setFormFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [fieldForm, setFieldForm] = useState<FormField>({
    id: '',
    type: 'text',
    label: '',
    required: false,
    options: [],
    placeholder: ''
  });

  useEffect(() => {
    fetchBenefits();
  }, []);

  const fetchBenefits = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching benefits...');
      const data = await benefitsService.getAll();

      // Parse JSON fields for each benefit
      const parsedBenefits = (Array.isArray(data) ? data : []).map((benefit: any) => {
        const parsed = { ...benefit };

        // Parse redemptionForm if it's a string
        const redemptionFormData = benefit.redemptionForm || benefit.redemption_form;
        if (redemptionFormData && typeof redemptionFormData === 'string') {
          try {
            parsed.redemptionForm = JSON.parse(redemptionFormData);
          } catch (e) {
            console.error('Error parsing redemptionForm:', e);
            parsed.redemptionForm = [];
          }
        } else if (Array.isArray(redemptionFormData)) {
          parsed.redemptionForm = redemptionFormData;
        } else {
          parsed.redemptionForm = [];
        }

        // Parse metadata if it's a string
        if (benefit.metadata && typeof benefit.metadata === 'string') {
          try {
            parsed.metadata = JSON.parse(benefit.metadata);
          } catch (e) {
            console.error('Error parsing metadata:', e);
          }
        }

        return parsed;
      });

      setBenefits(parsedBenefits);
      console.log('Benefits loaded successfully:', parsedBenefits);
    } catch (err: any) {
      console.log('Benefits API failed:', err.message);
      setError('Failed to load benefits');
      setBenefits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBenefit = async () => {
    try {
      setCreating(true);
      setError(null);
      
      // Prepare the benefit data with all fields
      const benefitData = {
        name: formData.name,
        description: formData.description,
        is_active: formData.isActive,
        max_redemptions_per_user: formData.maxRedemptionsPerUser,
        visible_to_plans: formData.visible_to_plans ? JSON.stringify(formData.visible_to_plans) : JSON.stringify(['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE']),
        // Store redemptionForm in its own column (redemption_form exists in DB)
        redemption_form: formData.redemptionForm ? JSON.stringify(formData.redemptionForm) : null,
        // Store all other data in metadata JSONB field
        metadata: JSON.stringify({
          companyInfo: {
            name: formData.companyName,
            website: formData.companyWebsite,
            email: formData.companyEmail,
            phone: formData.companyPhone,
            address: formData.companyAddress,
            logo: formData.companyLogo
          },
          partnerCredentials: {
            loginEmail: formData.partnerLoginEmail,
            loginPassword: formData.partnerLoginPassword
          },
          benefitDetails: {
            type: formData.benefitType,
            value: formData.benefitValue,
            redemptionDetails: formData.redemptionDetails,
            howToRedeem: formData.howToRedeem,
            highlights: formData.highlights
          },
          terms: formData.termsAndConditions,
          validity: {
            from: formData.validFrom,
            to: formData.validTo
          }
        })
      };

      const response = await benefitsService.create(benefitData);
      console.log('Benefit created successfully:', response);
      
      setCreateModalOpen(false);
      resetForm();
      fetchBenefits();
    } catch (err: any) {
      console.error('Create benefit error:', err);
      setError(`Failed to create benefit: ${err.response?.data?.error || err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleEditBenefit = (benefit: Benefit) => {
    setEditingBenefit(benefit);
    // Parse metadata if it exists
    let parsedData = { ...benefit };
    if (benefit.metadata && typeof benefit.metadata === 'string') {
      try {
        const metadata = JSON.parse(benefit.metadata);
        parsedData = {
          ...benefit,
          companyName: metadata.companyInfo?.name || benefit.partner,
          companyWebsite: metadata.companyInfo?.website,
          companyEmail: metadata.companyInfo?.email,
          companyPhone: metadata.companyInfo?.phone,
          companyAddress: metadata.companyInfo?.address,
          companyLogo: metadata.companyInfo?.logo,
          partnerLoginEmail: metadata.partnerCredentials?.loginEmail || '',
          partnerLoginPassword: metadata.partnerCredentials?.loginPassword || '',
          benefitType: metadata.benefitDetails?.type || 'discount',
          benefitValue: metadata.benefitDetails?.value || benefit.discount,
          redemptionDetails: metadata.benefitDetails?.redemptionDetails,
          howToRedeem: metadata.benefitDetails?.howToRedeem,
          highlights: metadata.benefitDetails?.highlights || [],
          termsAndConditions: metadata.terms,
          validFrom: metadata.validity?.from,
          validTo: metadata.validity?.to
        };
      } catch (e) {
        console.error('Error parsing metadata:', e);
      }
    }

    // Parse visible_to_plans if it exists and is a string
    const visibleToPlansData = (benefit as any).visible_to_plans;
    if (visibleToPlansData && typeof visibleToPlansData === 'string') {
      try {
        parsedData.visible_to_plans = JSON.parse(visibleToPlansData);
      } catch (e) {
        console.error('Error parsing visible_to_plans:', e);
        parsedData.visible_to_plans = ['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE'];
      }
    } else if (Array.isArray(visibleToPlansData)) {
      parsedData.visible_to_plans = visibleToPlansData;
    } else {
      // Default to all plans if not set
      parsedData.visible_to_plans = ['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE'];
    }

    // Parse redemptionForm if it exists as a string (check both redemptionForm and redemption_form)
    const redemptionFormData = benefit.redemptionForm || (benefit as any).redemption_form;
    if (redemptionFormData && typeof redemptionFormData === 'string') {
      try {
        parsedData.redemptionForm = JSON.parse(redemptionFormData);
      } catch (e) {
        console.error('Error parsing redemptionForm:', e);
        parsedData.redemptionForm = [];
      }
    } else if (Array.isArray(redemptionFormData)) {
      // Already an array, use directly
      parsedData.redemptionForm = redemptionFormData;
    } else {
      // Fallback to empty array if redemptionForm is not valid
      parsedData.redemptionForm = [];
    }

    setFormData(parsedData);
    setTabValue(0);
    setEditModalOpen(true);
  };

  const handleUpdateBenefit = async () => {
    try {
      setEditing(true);
      setError(null);
      
      const benefitData = {
        name: formData.name,
        description: formData.description,
        is_active: formData.isActive,
        max_redemptions_per_user: formData.maxRedemptionsPerUser,
        visible_to_plans: formData.visible_to_plans ? JSON.stringify(formData.visible_to_plans) : JSON.stringify(['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE']),
        // Store redemptionForm in its own column (redemption_form exists in DB)
        redemption_form: formData.redemptionForm ? JSON.stringify(formData.redemptionForm) : null,
        // Store all other data in metadata JSONB field
        metadata: JSON.stringify({
          companyInfo: {
            name: formData.companyName,
            website: formData.companyWebsite,
            email: formData.companyEmail,
            phone: formData.companyPhone,
            address: formData.companyAddress,
            logo: formData.companyLogo
          },
          partnerCredentials: {
            loginEmail: formData.partnerLoginEmail,
            loginPassword: formData.partnerLoginPassword
          },
          benefitDetails: {
            type: formData.benefitType,
            value: formData.benefitValue,
            redemptionDetails: formData.redemptionDetails,
            howToRedeem: formData.howToRedeem,
            highlights: formData.highlights
          },
          terms: formData.termsAndConditions,
          validity: {
            from: formData.validFrom,
            to: formData.validTo
          }
        })
      };

      const response = await benefitsService.update(editingBenefit!.id, benefitData);
      console.log('Benefit updated successfully:', response);
      
      setEditModalOpen(false);
      setEditingBenefit(null);
      resetForm();
      fetchBenefits();
    } catch (err: any) {
      console.error('Update benefit error:', err);
      setError(`Failed to update benefit: ${err.response?.data?.error || err.message}`);
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteBenefit = (benefitId: string) => {
    setBenefitToDelete(benefitId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!benefitToDelete) return;
    
    try {
      setDeleting(true);
      setError(null);
      
      await benefitsService.delete(benefitToDelete);
      console.log('Benefit deleted successfully');
      
      setDeleteConfirmOpen(false);
      setBenefitToDelete(null);
      fetchBenefits();
    } catch (err: any) {
      console.error('Delete benefit error:', err);
      setError(`Failed to delete benefit: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      companyName: '',
      companyWebsite: '',
      companyEmail: '',
      companyPhone: '',
      companyAddress: '',
      benefitType: 'discount',
      benefitValue: '',
      redemptionDetails: '',
      howToRedeem: '',
      termsAndConditions: '',
      validFrom: new Date().toISOString().split('T')[0],
      validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      maxRedemptionsPerUser: 1,
      highlights: [''],
      isActive: true,
      visible_to_plans: ['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE']
    });
    setTabValue(0);
  };

  const addHighlight = () => {
    setFormData({
      ...formData,
      highlights: [...(formData.highlights || []), '']
    });
  };

  const updateHighlight = (index: number, value: string) => {
    const newHighlights = [...(formData.highlights || [])];
    newHighlights[index] = value;
    setFormData({ ...formData, highlights: newHighlights });
  };

  const removeHighlight = (index: number) => {
    const newHighlights = (formData.highlights || []).filter((_, i) => i !== index);
    setFormData({ ...formData, highlights: newHighlights });
  };

  const getBenefitTypeLabel = (type?: string) => {
    switch (type) {
      case 'discount': return 'Discount';
      case 'free_service': return 'Free Service';
      case 'voucher': return 'Voucher';
      case 'membership': return 'Membership';
      case 'consultation': return 'Consultation';
      default: return 'Benefit';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#FFD700' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Benefits Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setCreateModalOpen(true);
          }}
          sx={{ height: 'fit-content' }}
        >
          Create New Benefit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {benefits.map((benefit) => (
          <Grid item xs={12} md={6} lg={4} key={benefit.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  {(() => {
                    // Try to get logo from metadata
                    let logo = null;
                    if (benefit.metadata && typeof benefit.metadata === 'string') {
                      try {
                        const metadata = JSON.parse(benefit.metadata);
                        logo = metadata.companyInfo?.logo;
                      } catch (e) {}
                    } else if (benefit.metadata && typeof benefit.metadata === 'object') {
                      logo = (benefit.metadata as any).companyInfo?.logo;
                    }

                    return logo ? (
                      <Avatar
                        src={logo}
                        alt={benefit.name}
                        sx={{ width: 56, height: 56 }}
                        variant="rounded"
                      />
                    ) : (
                      <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }} variant="rounded">
                        <CardGiftcardIcon />
                      </Avatar>
                    );
                  })()}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2">
                      {benefit.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.companyName || benefit.partner || 'Partner'}
                    </Typography>
                  </Box>
                </Stack>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {benefit.description}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  {benefit.benefitType && (
                    <Chip 
                      label={getBenefitTypeLabel(benefit.benefitType)} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                  {benefit.benefitValue || benefit.discount ? (
                    <Chip 
                      label={benefit.benefitValue || benefit.discount} 
                      size="small" 
                      color="success"
                      icon={<LocalOfferIcon />}
                    />
                  ) : null}
                </Stack>

                {benefit.highlights && benefit.highlights.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {benefit.highlights.slice(0, 2).map((highlight, idx) => (
                      <Typography key={idx} variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                        {highlight}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  {benefit.validTo && (
                    <Typography variant="caption" color="text.secondary">
                      <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      Valid until {new Date(benefit.validTo).toLocaleDateString()}
                    </Typography>
                  )}
                </Stack>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Chip
                  label={benefit.isActive ? 'Active' : 'Inactive'}
                  color={benefit.isActive ? 'success' : 'default'}
                  size="small"
                />
                <Box>
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => handleEditBenefit(benefit)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleDeleteBenefit(benefit.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Benefit Modal */}
      <Dialog
        open={createModalOpen || editModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditModalOpen(false);
          setEditingBenefit(null);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editModalOpen ? 'Edit Benefit' : 'Create New Benefit'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="Basic Info" />
              <Tab label="Company Details" />
              <Tab label="Benefit Details" />
              <Tab label="Terms & Validity" />
              <Tab label="📝 Redemption Form" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Benefit Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., Free Financial Consultation"
                  helperText="The main title users will see"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Short Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  required
                  placeholder="Brief overview of the benefit"
                  helperText="A concise description for the benefit card"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <TextField
                    select
                    label="Benefit Type"
                    value={formData.benefitType}
                    onChange={(e) => setFormData({ ...formData, benefitType: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="discount">Discount</option>
                    <option value="free_service">Free Service</option>
                    <option value="voucher">Voucher</option>
                    <option value="membership">Membership</option>
                    <option value="consultation">Consultation</option>
                  </TextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Benefit Value"
                  value={formData.benefitValue}
                  onChange={(e) => setFormData({ ...formData, benefitValue: e.target.value })}
                  fullWidth
                  placeholder="e.g., Free consultation worth R500"
                  helperText="What the user gets"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Visible To Plans *</InputLabel>
                  <Select
                    multiple
                    value={formData.visible_to_plans || []}
                    onChange={(e) => setFormData({ ...formData, visible_to_plans: e.target.value as string[] })}
                    label="Visible To Plans *"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {['FREE', 'ESSENTIAL', 'PREMIUM', 'ULTIMATE'].map(plan => (
                      <MenuItem key={plan} value={plan}>
                        <Checkbox checked={(formData.visible_to_plans || []).includes(plan)} />
                        <ListItemText primary={plan} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select which membership plans can see and redeem this benefit
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active (Available for redemption)"
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Company/Partner Name"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  fullWidth
                  placeholder="e.g., Leah And Levi Financial Services"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company Website"
                  value={formData.companyWebsite}
                  onChange={(e) => setFormData({ ...formData, companyWebsite: e.target.value })}
                  fullWidth
                  placeholder="https://www.example.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact Email"
                  value={formData.companyEmail}
                  onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })}
                  fullWidth
                  type="email"
                  placeholder="contact@example.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact Phone"
                  value={formData.companyPhone}
                  onChange={(e) => setFormData({ ...formData, companyPhone: e.target.value })}
                  fullWidth
                  placeholder="+27 11 123 4567"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company Address"
                  value={formData.companyAddress}
                  onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })}
                  fullWidth
                  placeholder="123 Main St, City"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <TextField
                    label="Logo URL"
                    value={formData.companyLogo}
                    onChange={(e) => setFormData({ ...formData, companyLogo: e.target.value })}
                    fullWidth
                    placeholder="https://example.com/logo.png"
                    helperText="Paste image URL or upload a logo below"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ImageIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                    >
                      Upload Logo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setFormData({ ...formData, companyLogo: reader.result as string });
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </Button>

                    {formData.companyLogo && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img
                          src={formData.companyLogo}
                          alt="Logo preview"
                          style={{
                            maxWidth: '100px',
                            maxHeight: '60px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '4px'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setFormData({ ...formData, companyLogo: '' })}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Partner Dashboard Login Credentials */}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🔐 Partner Dashboard Access
                  </Typography>
                  <Typography variant="body2">
                    Create login credentials for this partner to access their own dashboard and view redemptions.
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Partner Login Email"
                  value={formData.partnerLoginEmail}
                  onChange={(e) => setFormData({ ...formData, partnerLoginEmail: e.target.value })}
                  fullWidth
                  type="email"
                  placeholder="partner@company.com"
                  helperText="Email for partner dashboard login"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Partner Login Password"
                  value={formData.partnerLoginPassword}
                  onChange={(e) => setFormData({ ...formData, partnerLoginPassword: e.target.value })}
                  fullWidth
                  type="password"
                  placeholder="Create a secure password"
                  helperText="Password for partner dashboard login"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Detailed Redemption Benefits"
                  value={formData.redemptionDetails}
                  onChange={(e) => setFormData({ ...formData, redemptionDetails: e.target.value })}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Explain in detail what the user will receive when they redeem this benefit..."
                  helperText="Be specific about what's included"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="How to Redeem"
                  value={formData.howToRedeem}
                  onChange={(e) => setFormData({ ...formData, howToRedeem: e.target.value })}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Step-by-step instructions on how users can redeem this benefit..."
                  helperText="Clear redemption instructions"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Key Highlights (Bullet Points)
                </Typography>
                {formData.highlights?.map((highlight, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={highlight}
                      onChange={(e) => updateHighlight(index, e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="e.g., Free will drafting service"
                    />
                    <IconButton 
                      size="small" 
                      onClick={() => removeHighlight(index)}
                      disabled={formData.highlights?.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addHighlight}
                  sx={{ mt: 1 }}
                >
                  Add Highlight
                </Button>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valid From"
                  type="date"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valid Until"
                  type="date"
                  value={formData.validTo}
                  onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Max Redemptions Per User"
                  type="number"
                  value={formData.maxRedemptionsPerUser}
                  onChange={(e) => setFormData({ ...formData, maxRedemptionsPerUser: parseInt(e.target.value) || 1 })}
                  fullWidth
                  inputProps={{ min: 1 }}
                  helperText="How many times can each user redeem"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Terms and Conditions"
                  value={formData.termsAndConditions}
                  onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Enter all terms, conditions, and restrictions..."
                  helperText="Important terms users must agree to"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 4: Redemption Form Builder */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ p: 2, bgcolor: 'rgba(255, 215, 0, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 215, 0, 0.2)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" color="primary">📝 Redemption Form Builder</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Configure fields that users must fill when redeeming this benefit
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingField(null);
                    setFieldForm({
                      id: Date.now().toString(),
                      type: 'text',
                      label: '',
                      required: false,
                      options: [],
                      placeholder: ''
                    });
                    setFormFieldDialog(true);
                  }}
                  sx={{ bgcolor: '#FFD700', color: '#000', '&:hover': { bgcolor: '#E6C200' } }}
                >
                  Add Field
                </Button>
              </Box>

              {(!formData.redemptionForm || formData.redemptionForm.length === 0) ? (
                <Alert severity="info">
                  No redemption fields configured. Add fields that users will fill when redeeming this benefit.
                </Alert>
              ) : (
                <List>
                  {(formData.redemptionForm || []).map((field) => (
                    <ListItem key={field.id} divider>
                      <ListItemText
                        primary={field.label}
                        secondary={`Type: ${field.type}${field.required ? ' (Required)' : ''}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            setEditingField(field);
                            setFieldForm({ ...field });
                            setFormFieldDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              redemptionForm: formData.redemptionForm?.filter(f => f.id !== field.id) || []
                            });
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </TabPanel>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCreateModalOpen(false);
              setEditModalOpen(false);
              setEditingBenefit(null);
              resetForm();
            }} 
            disabled={creating || editing}
          >
            Cancel
          </Button>
          <Button
            onClick={editModalOpen ? handleUpdateBenefit : handleCreateBenefit}
            variant="contained"
            disabled={creating || editing || !formData.name || !formData.description}
            startIcon={(creating || editing) ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {editModalOpen ? (editing ? 'Updating...' : 'Update Benefit') : (creating ? 'Creating...' : 'Create Benefit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setBenefitToDelete(null);
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this benefit? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setBenefitToDelete(null);
            }}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Field Dialog */}
      <Dialog
        open={formFieldDialog}
        onClose={() => setFormFieldDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingField ? 'Edit Field' : 'Add Form Field'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Field Label *"
                  value={fieldForm.label}
                  onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., Full Name, Phone Number"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Field Type *</InputLabel>
                  <Select
                    value={fieldForm.type}
                    onChange={(e) => setFieldForm({ ...fieldForm, type: e.target.value as any })}
                    label="Field Type *"
                  >
                    <MenuItem value="text"><TextFieldsIcon sx={{ mr: 1, fontSize: 18 }} /> Text Input</MenuItem>
                    <MenuItem value="email"><EmailIcon sx={{ mr: 1, fontSize: 18 }} /> Email</MenuItem>
                    <MenuItem value="phone"><PhoneIcon sx={{ mr: 1, fontSize: 18 }} /> Phone</MenuItem>
                    <MenuItem value="number"><TextFieldsIcon sx={{ mr: 1, fontSize: 18 }} /> Number</MenuItem>
                    <MenuItem value="select"><SelectIcon sx={{ mr: 1, fontSize: 18 }} /> Dropdown</MenuItem>
                    <MenuItem value="radio"><RadioIcon sx={{ mr: 1, fontSize: 18 }} /> Radio Buttons</MenuItem>
                    <MenuItem value="checkbox"><CheckBoxIcon sx={{ mr: 1, fontSize: 18 }} /> Checkboxes</MenuItem>
                    <MenuItem value="textarea"><TextFieldsIcon sx={{ mr: 1, fontSize: 18 }} /> Text Area</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Placeholder"
                  value={fieldForm.placeholder || ''}
                  onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
                  fullWidth
                  placeholder="e.g., Enter your full name"
                />
              </Grid>
              {(fieldForm.type === 'select' || fieldForm.type === 'radio' || fieldForm.type === 'checkbox') && (
                <Grid item xs={12}>
                  <TextField
                    label="Options (comma separated) *"
                    value={fieldForm.options?.join(', ') || ''}
                    onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value.split(',').map(o => o.trim()) })}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="e.g., Option 1, Option 2, Option 3"
                    helperText="Enter options separated by commas"
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldForm.required}
                      onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                    />
                  }
                  label="Required Field"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormFieldDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (!fieldForm.label) {
                alert('Field label is required');
                return;
              }
              const newField = { ...fieldForm };
              if (!editingField) {
                newField.id = Date.now().toString();
              }
              setFormData({
                ...formData,
                redemptionForm: editingField
                  ? formData.redemptionForm?.map(f => f.id === editingField.id ? newField : f) || []
                  : [...(formData.redemptionForm || []), newField]
              });
              setFormFieldDialog(false);
            }}
            variant="contained"
          >
            {editingField ? 'Update' : 'Add'} Field
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BenefitsEnhanced;